import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import VideoCarousel from '../components/VideoCarousel';
import { BannerCarousel } from '../components/BannerCarousel';
import { getVideos } from '../api';
import { useAuth } from '../contexts/AuthContext';

export const HomePage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const search = searchParams.get('search') || '';
  const categoryFilter = searchParams.get('category') || '';
  const [loading, setLoading] = useState(true);
  
  // Sections data
  const [featuredVideos, setFeaturedVideos] = useState([]);
  const [newVideos, setNewVideos] = useState([]);
  const [trendingVideos, setTrendingVideos] = useState([]);
  const [popularVideos, setPopularVideos] = useState([]);
  const [soonVideos, setSoonVideos] = useState([]);
  
  // Genre sections
  const [musicVideos, setMusicVideos] = useState([]);
  const [sportsVideos, setSportsVideos] = useState([]);
  const [educationVideos, setEducationVideos] = useState([]);
  const [entertainmentVideos, setEntertainmentVideos] = useState([]);

  useEffect(() => {
    fetchAllSections();
  }, [user, search, categoryFilter]);

  const fetchAllSections = async () => {
    setLoading(true);
    try {
      if (search) {
        // Mode recherche : une seule requête
        const res = await getVideos({ search: search.trim(), limit: 50 });
        setNewVideos(res.data.videos);
        setFeaturedVideos([]);
        setTrendingVideos([]);
        setPopularVideos([]);
        setSoonVideos([]);
        setMusicVideos([]);
        setSportsVideos([]);
        setEducationVideos([]);
        setEntertainmentVideos([]);
        return;
      }

      if (categoryFilter) {
        // Mode filtre catégorie
        const res = await getVideos({ category: categoryFilter, limit: 50 });
        setNewVideos(res.data.videos);
        setFeaturedVideos([]);
        setTrendingVideos([]);
        setPopularVideos([]);
        setSoonVideos([]);
        setMusicVideos([]);
        setSportsVideos([]);
        setEducationVideos([]);
        setEntertainmentVideos([]);
        return;
      }

      // Mode normal : toutes les sections en parallèle
      const [
        featuredRes,
        newRes,
        trendingRes,
        popularRes,
        soonRes,
        musicRes,
        sportsRes,
        educationRes,
        entertainmentRes
      ] = await Promise.all([
        getVideos({ status: 'featured', limit: 10 }),
        getVideos({ sort: 'created_at', limit: 10 }),
        getVideos({ sort: 'trending', limit: 10 }),
        getVideos({ sort: 'views', limit: 10 }),
        getVideos({ status: 'soon', limit: 10 }),
        getVideos({ category: 'music', limit: 10 }),
        getVideos({ category: 'sports', limit: 10 }),
        getVideos({ category: 'education', limit: 10 }),
        getVideos({ category: 'entertainment', limit: 10 })
      ]);

      setFeaturedVideos(featuredRes.data.videos);
      setNewVideos(newRes.data.videos);
      setTrendingVideos(trendingRes.data.videos);
      setPopularVideos(popularRes.data.videos);
      setSoonVideos(soonRes.data.videos);
      setMusicVideos(musicRes.data.videos);
      setSportsVideos(sportsRes.data.videos);
      setEducationVideos(educationRes.data.videos);
      setEntertainmentVideos(entertainmentRes.data.videos);
    } catch (error) {
      console.error('Failed to fetch sections:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      {/* Banner Carousel - Full width */}
      <div className="mb-8">
        <BannerCarousel videos={featuredVideos.length > 0 ? featuredVideos : newVideos.slice(0, 5)} />
      </div>

      <div className="container max-w-[1800px] mx-auto">
        {/* Search/Filter Results Message */}
        {(search || categoryFilter) && (
          <div className="mb-4 px-4 md:px-8">
            <p className="text-muted-foreground">
              {search ? `Résultats pour "${search}"` : `Catégorie : ${categoryFilter}`}
            </p>
          </div>
        )}

        {/* Sections Carousels */}
        <div className="space-y-8">
          {/* New Section */}
          {newVideos.length > 0 && (
            <VideoCarousel
              title={search ? "📺 Résultats" : "🆕 Nouveau"}
              videos={newVideos}
              loading={loading}
            />
          )}

          {/* Trending Section */}
          {trendingVideos.length > 0 && !search && (
            <VideoCarousel
              title="🔥 Tendances"
              videos={trendingVideos}
              loading={loading}
            />
          )}

          {/* Popular Section */}
          {popularVideos.length > 0 && !search && (
            <VideoCarousel
              title="⭐ Populaires"
              videos={popularVideos}
              loading={loading}
            />
          )}

          {/* Featured Section */}
          {featuredVideos.length > 0 && !search && (
            <VideoCarousel
              title="🎪 À la Une"
              videos={featuredVideos}
              loading={loading}
            />
          )}

          {/* Coming Soon / Bande-annonces */}
          {soonVideos.length > 0 && !search && (
            <VideoCarousel
              title="🎞️ Bientôt"
              videos={soonVideos}
              loading={loading}
            />
          )}

          {/* Genre Sections */}
          {musicVideos.length > 0 && !search && (
            <VideoCarousel
              title="🎵 Musique"
              videos={musicVideos}
              loading={loading}
            />
          )}

          {sportsVideos.length > 0 && !search && (
            <VideoCarousel
              title="⚽ Sports"
              videos={sportsVideos}
              loading={loading}
            />
          )}

          {educationVideos.length > 0 && !search && (
            <VideoCarousel
              title="📚 Éducation"
              videos={educationVideos}
              loading={loading}
            />
          )}

          {entertainmentVideos.length > 0 && !search && (
            <VideoCarousel
              title="🎭 Divertissement"
              videos={entertainmentVideos}
              loading={loading}
            />
          )}
        </div>

        {/* Empty State */}
        {!loading && newVideos.length === 0 && trendingVideos.length === 0 && (
          <div className="text-center py-12 px-4">
            <p className="text-muted-foreground text-lg">
              {search ? "Aucune vidéo trouvée" : "Aucun contenu disponible pour le moment"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
